import MenuIcon from '@mui/icons-material/Menu'
import MenuOpenIcon from '@mui/icons-material/MenuOpen'
import MuiAppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import { styled, useTheme } from '@mui/material/styles'
import Toolbar from '@mui/material/Toolbar'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import * as React from 'react'

import type { UserInfo } from '../types/auth'
import UserMenu from './UserMenu'

// import ThemeSwitcher from './ThemeSwitcher';

const AppBar = styled(MuiAppBar)(({ theme }) => ({
  borderWidth: 0,
  borderBottomWidth: 1,
  borderStyle: 'solid',
  borderColor: (theme.vars ?? theme).palette.divider,
  boxShadow: 'none',
  zIndex: theme.zIndex.drawer + 1,
}))

const LogoContainer = styled('div')({
  position: 'relative',
  height: 40,
  display: 'flex',
  alignItems: 'center',
  '& img': {
    maxHeight: 40,
  },
})

export interface PageHeaderProps {
  logo?: React.ReactNode
  title?: string
  menuOpen: boolean
  onToggleMenu: (open: boolean) => void
  cognitoUser?: UserInfo
  onSignOut?: () => void
}

export default function PageHeader({ logo, title, menuOpen, onToggleMenu, cognitoUser, onSignOut }: PageHeaderProps) {
  const theme = useTheme()
  const [anchorElUser, setAnchorElUser] = React.useState<HTMLElement | null>(null)

  const handleMenuOpen = React.useCallback(() => {
    onToggleMenu(!menuOpen)
  }, [menuOpen, onToggleMenu])

  const handleOpenUserMenu = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget)
  }, [])

  const handleCloseUserMenu = React.useCallback(() => {
    setAnchorElUser(null)
  }, [])

  const handleLogout = React.useCallback(() => {
    handleCloseUserMenu()
    onSignOut?.()
  }, [handleCloseUserMenu, onSignOut])

  const getMenuIcon = React.useCallback(
    (isExpanded: boolean) => {
      const expandMenuActionText = 'Expand'
      const collapseMenuActionText = 'Collapse'

      return (
        <Tooltip title={`${isExpanded ? collapseMenuActionText : expandMenuActionText} menu`} enterDelay={1000}>
          <div>
            <IconButton
              size="small"
              aria-label={`${isExpanded ? collapseMenuActionText : expandMenuActionText} navigation menu`}
              onClick={handleMenuOpen}
            >
              {isExpanded ? <MenuOpenIcon /> : <MenuIcon />}
            </IconButton>
          </div>
        </Tooltip>
      )
    },
    [handleMenuOpen]
  )

  return (
    <AppBar color="inherit" position="absolute" sx={{ displayPrint: 'none' }}>
      <Toolbar sx={{ backgroundColor: 'inherit', mx: { xs: -0.75, sm: -1 } }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            flexWrap: 'wrap',
            width: '100%',
          }}
        >
          <Stack direction="row" alignItems="center">
            <Box sx={{ mr: 1 }}>{getMenuIcon(menuOpen)}</Box>
            {/* <Link to="/" style={{ textDecoration: 'none' }}> */}
            <Stack direction="row" alignItems="center">
              {logo ? <LogoContainer>{logo}</LogoContainer> : null}
              {title ? (
                <Typography
                  variant="h6"
                  sx={{
                    color: (theme.vars ?? theme).palette.text.secondary,
                    fontWeight: '700',
                    ml: 1,
                    whiteSpace: 'nowrap',
                    lineHeight: 1,
                  }}
                >
                  {title}
                </Typography>
              ) : null}
            </Stack>
            {/* </Link> */}
          </Stack>
          <UserMenu
            cognitoUser={cognitoUser}
            anchorElUser={anchorElUser}
            handleOpenUserMenu={handleOpenUserMenu}
            handleCloseUserMenu={handleCloseUserMenu}
            handleLogout={onSignOut ? handleLogout : undefined}
          />
        </Stack>
      </Toolbar>
    </AppBar>
  )
}
